import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFlagsReady } from '../lib/connect';
import { projectDataClient } from '../lib/connect/clients';
function mapSectionToOutline(node) {
    return {
        sectionId: node.sectionId,
        title: node.title,
        description: node.description ?? '',
        parentSectionId: node.parentSectionId ?? '',
        sortOrder: node.sortOrder ?? node.level,
        taggedFileIds: [],
        requirementIds: [],
        children: node.children?.map(mapSectionToOutline)
    };
}
/**
 * Fetch outline sections for a proposal project via Connect RPC.
 * When volumeId is provided, only returns sections for that volume.
 */ export function useOutlineSections(projectId, volumeId) {
    const flagsReady = useFlagsReady();
    return useQuery({
        queryKey: [
            'outline-sections',
            projectId,
            volumeId ?? ''
        ],
        queryFn: async ()=>{
            const response = await projectDataClient.getSectionMap({
                projectId,
                volumeId: volumeId ?? ''
            });
            return (response.sections ?? []).map(mapSectionToOutline);
        },
        enabled: !!projectId && flagsReady,
        staleTime: 60000
    });
}
/**
 * Fetch writing plan for a proposal project via Connect RPC.
 */ export function useWritingPlan(projectId) {
    const flagsReady = useFlagsReady();
    return useQuery({
        queryKey: [
            'writing-plan',
            projectId
        ],
        queryFn: async ()=>{
            // UpsertWritingPlan returns the plan — use it as a read too
            // For now return empty until a plan exists
            const empty = [];
            return empty;
        },
        enabled: !!projectId && flagsReady,
        staleTime: 60000
    });
}
/**
 * Update an outline section via Connect RPC.
 */ export function useUpdateOutlineSection(projectId, volumeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (params)=>{
            return projectDataClient.updateOutlineSection({
                projectId,
                sectionId: params.sectionId,
                title: params.updates.title ?? '',
                description: params.updates.description ?? ''
            });
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'outline-sections',
                    projectId,
                    volumeId ?? ''
                ]
            });
        }
    });
}
/**
 * Add a new outline section via Connect RPC.
 * Uses UpdateOutlineSection with a new sectionId.
 */ export function useAddOutlineSection(projectId, volumeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (params)=>{
            return projectDataClient.updateOutlineSection({
                projectId,
                sectionId: '',
                title: params.title ?? 'New Section',
                description: '',
                parentSectionId: params.parentSectionId ?? '',
                volumeId: volumeId ?? ''
            });
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'outline-sections',
                    projectId,
                    volumeId ?? ''
                ]
            });
        }
    });
}
/**
 * Delete an outline section via Connect RPC.
 */ export function useDeleteOutlineSection(projectId, volumeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (sectionId)=>{
            return projectDataClient.deleteOutlineSection({
                projectId,
                sectionId
            });
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'outline-sections',
                    projectId,
                    volumeId ?? ''
                ]
            });
        }
    });
}
/**
 * Reorder an outline section.
 * Updates sort_order via UpdateOutlineSection.
 */ export function useReorderOutlineSection(projectId, volumeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (params)=>{
            return projectDataClient.updateOutlineSection({
                projectId,
                sectionId: params.sectionId,
                sortOrder: params.direction === 'up' ? -1 : 1
            });
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'outline-sections',
                    projectId,
                    volumeId ?? ''
                ]
            });
        }
    });
}
/**
 * Update a writing plan section via Connect RPC.
 */ export function useUpdateWritingPlan(projectId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (params)=>{
            return projectDataClient.upsertWritingPlan({
                projectId,
                sections: [
                    {
                        sectionId: params.sectionId,
                        outlineSectionId: params.updates.outlineSectionId ?? params.sectionId,
                        title: params.updates.title ?? '',
                        instructions: params.updates.instructions ?? '',
                        status: params.updates.status ?? 'not_started',
                        assignedRequirementIds: params.updates.assignedRequirementIds ?? [],
                        targetPageCount: params.updates.targetPageCount ?? 0
                    }
                ]
            });
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'writing-plan',
                    projectId
                ]
            });
        }
    });
}
